import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DomainType } from '../../types';
import { getDomainLabel, isAIOEnabled } from '@/config/domainContent';
import { useMetadata } from '@/api/queries';
import digitalFootprintIcon from '@/assets/digital-footprint-icon.svg';

interface NavbarProps {
  domain?: DomainType;
}

type NavItem =
  | { label: string; kind: 'link'; to: string; active: boolean }
  | { label: string; kind: 'action'; onClick: () => void };

export default function Navbar({ domain }: NavbarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: metadata } = useMetadata();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const getDomainPrefix = () => {
    if (!domain) return '';
    return domain;
  };

  const prefix = getDomainPrefix();
  const navTitle = domain ? getDomainLabel(domain) : 'Digital Footprint';
  const titleLink = domain ? `/${prefix}-home` : '/';

  const renderNavTitle = () => {
    const match = navTitle.match(/^(.*?)(\s*Footprint)(.*)$/i);
    if (!match) return navTitle;
    const [, before, footprintPart, after] = match;
    return (
      <>
        {before}
        <Box component="span" sx={{ color: '#ffa200' }}>
          {footprintPart}
        </Box>
        {after}
      </>
    );
  };

  const brandingLinkSx = {
    flexShrink: 0,
    textDecoration: 'none',
    color: '#ffffff',
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem', lg: '1.5rem' },
    '&:hover': { opacity: 0.92 },
  } as const;
  const domainUpdatedAt = domain ? metadata?.domain_updates?.[domain] : undefined;
  const updatedAtToShow = domainUpdatedAt ?? metadata?.update_date;

  const formatLastUpdated = (updateDate?: string) => {
    if (!updateDate) return 'Unknown';
    const parsedDate = new Date(updateDate);
    if (Number.isNaN(parsedDate.getTime())) return updateDate;
    return parsedDate.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const scrollToLandingSection = (sectionId: string) => {
    if (location.pathname === '/') {
      const sectionElement = document.getElementById(sectionId);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    navigate(`/#${sectionId}`);
  };

  const landingItems: NavItem[] = [
    { label: 'Explore', kind: 'action', onClick: () => scrollToLandingSection('explore') },
    { label: 'Why visibility matters', kind: 'action', onClick: () => scrollToLandingSection('why-visibility-matters') },
    { label: 'How it works', kind: 'action', onClick: () => scrollToLandingSection('how-it-works') },
    { label: 'Methodology', kind: 'action', onClick: () => scrollToLandingSection('methodology') },
  ];

  const domainItems: NavItem[] = domain
    ? [
        { label: 'Home', kind: 'link', to: `/${prefix}-home`, active: isActive(`/${prefix}-home`) },
        { label: 'Topics Ranking', kind: 'link', to: `/${prefix}-term-search`, active: isActive(`/${prefix}-term-search`) },
        { label: 'Actors Ranking', kind: 'link', to: `/${prefix}-positon-graph`, active: isActive(`/${prefix}-positon-graph`) },
        { label: 'Actors Analysis', kind: 'link', to: `/${prefix}-compare`, active: isActive(`/${prefix}-compare`) },
        ...(isAIOEnabled(domain)
          ? [
              {
                label: 'AI Overview',
                kind: 'link' as const,
                to: `/${prefix}-ai-overview`,
                active: isActive(`/${prefix}-ai-overview`),
              },
            ]
          : []),
        { label: 'List of Topics', kind: 'link', to: `/${prefix}-topics`, active: isActive(`/${prefix}-topics`) },
      ]
    : [];

  const primaryItems: NavItem[] = domain ? domainItems : landingItems;
  const allFootprintsItem: NavItem | null = domain
    ? { label: 'All Footprints', kind: 'link', to: '/', active: isActive('/') }
    : null;

  const renderDesktopButton = (item: NavItem) => {
    const baseSx = {
      color: 'white',
      whiteSpace: 'nowrap',
      flexShrink: 0,
    } as const;
    if (item.kind === 'link') {
      return (
        <Button
          key={item.label}
          component={Link}
          to={item.to}
          color="inherit"
          sx={{
            ...baseSx,
            backgroundColor: item.active ? 'rgba(255,255,255,0.1)' : 'transparent',
          }}
        >
          {item.label}
        </Button>
      );
    }
    return (
      <Button key={item.label} color="inherit" sx={baseSx} onClick={item.onClick}>
        {item.label}
      </Button>
    );
  };

  const handleMobileItemClick = (item: NavItem) => {
    setMobileOpen(false);
    if (item.kind === 'action') {
      item.onClick();
    }
  };

  const renderMobileItem = (item: NavItem) => {
    const isLink = item.kind === 'link';
    const isItemActive = isLink && item.active;
    return (
      <ListItem key={item.label} disablePadding>
        <ListItemButton
          {...(isLink ? { component: Link, to: item.to } : {})}
          onClick={() => handleMobileItemClick(item)}
          sx={{
            backgroundColor: isItemActive ? 'rgba(26, 115, 232, 0.08)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(26, 115, 232, 0.12)',
            },
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              fontWeight: isItemActive ? 700 : 500,
              color: '#1f1f1f',
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <AppBar position="sticky" elevation={0} sx={{ top: 0, zIndex: (t) => t.zIndex.appBar }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ gap: 2 }}>
          <Box
            sx={{
              flexGrow: { xs: 1, md: 0 },
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              mr: { xs: 2, md: 3 },
            }}
          >
            <Box
              component={Link}
              to="/"
              aria-label="All Footprints"
              sx={brandingLinkSx}
            >
              <Box
                component="img"
                src={digitalFootprintIcon}
                alt=""
                aria-hidden
                sx={{
                  display: 'block',
                  height: { xs: 28, sm: 30, md: 34, lg: 38 },
                  width: 'auto',
                  flexShrink: 0,
                }}
              />
            </Box>
            <Typography
              component={Link}
              to={titleLink}
              sx={brandingLinkSx}
            >
              {renderNavTitle()}
            </Typography>
          </Box>

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: domain ? 0 : 1,
              flexGrow: domain ? 1 : 0,
            }}
          >
            {primaryItems.map(renderDesktopButton)}
          </Box>

          {domain && (
            <Box
              sx={{
                ml: 'auto',
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, whiteSpace: 'nowrap' }}>
                Last updated: {formatLastUpdated(updatedAtToShow)}
              </Typography>
              {allFootprintsItem && renderDesktopButton(allFootprintsItem)}
            </Box>
          )}

          <IconButton
            aria-label="Open navigation menu"
            edge="end"
            onClick={() => setMobileOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: 'white',
              ml: 'auto',
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: 280, boxSizing: 'border-box' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: '#1f1f1f' }}>Menu</Typography>
          <IconButton aria-label="Close navigation menu" onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {primaryItems.map(renderMobileItem)}
          {allFootprintsItem && (
            <>
              <Divider sx={{ my: 1 }} />
              {renderMobileItem(allFootprintsItem)}
            </>
          )}
        </List>
        {domain && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#5f6368' }}>
                Last updated: {formatLastUpdated(updatedAtToShow)}
              </Typography>
            </Box>
          </>
        )}
      </Drawer>
    </AppBar>
  );
}
